import { c as _c } from "react/compiler-runtime";
import { IcoEye16, IcoEyeCrossed16 } from '@onefootprint/icons';
import { useEffect, useRef, useState } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IconButton from '../../icon-button';
import FormInput, { type FormInputProps } from './form-input';
export type FormSsn9Props<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  /**
   * The scrubbed ssn, only showing the last 4 digits. Only populated if the SSN9 cannot be fully decrypted.
   *
   * This form component will leave the form field's value as `undefined` if it is identical to the `scrubbedSsn9Value`.
   * */
  scrubbedSsn9Value?: string;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  defaultVisible?: boolean;
  /** @deprecated No longer used; FE validation relaxed in favor of backend. Kept for API compatibility. */
  useLenientValidation?: boolean;
} & Omit<FormInputProps<TFieldValues>, 'name' | 'rules' | 'type' | 'autoComplete' | 'placeholder'>;
const SSN9_FORMAT = /^\d{3}-?\d{2}-?\d{4}$|^\d{9}$/;
export default function FormSsn9(t0) {
  const $ = _c(52);
  let props;
  let rules;
  let scrubbedSsn9Value;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      rules: t3,
      size: t4,
      defaultVisible: t5,
      scrubbedSsn9Value: t6,
      useLenientValidation: _useLenientValidation,
      ...t7
    } = t0;
    rules = t3;
    t1 = t4;
    t2 = t5;
    scrubbedSsn9Value = t6;
    props = t7;
    $[0] = t0;
    $[1] = props;
    $[2] = rules;
    $[3] = scrubbedSsn9Value;
    $[4] = t1;
    $[5] = t2;
  } else {
    props = $[1];
    rules = $[2];
    scrubbedSsn9Value = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const size = t1 === undefined ? "default" : t1;
  const defaultVisible = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation("ui");
  const [isVisible, setIsVisible] = useState(defaultVisible);
  const {
    setValue
  } = useFormContext();
  const isInitialValueScrubbed = !!scrubbedSsn9Value;
  const [showScrubbedValue, setShowScrubbedValue] = useState(isInitialValueScrubbed);
  const previousScrubbedValue = useRef(scrubbedSsn9Value);
  let t3;
  let t4;
  if ($[6] !== props.name || $[7] !== scrubbedSsn9Value || $[8] !== setValue) {
    t3 = () => {
      if (scrubbedSsn9Value && scrubbedSsn9Value !== previousScrubbedValue.current) {
        setShowScrubbedValue(true);
        setValue(props.name, scrubbedSsn9Value as PathValue<TFieldValues, Path<TFieldValues>>);
      }
      previousScrubbedValue.current = scrubbedSsn9Value;
    };
    t4 = [scrubbedSsn9Value, props.name, setValue];
    $[6] = props.name;
    $[7] = scrubbedSsn9Value;
    $[8] = setValue;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== t) {
    t5 = value => {
      if (!value) {
        return true;
      }
      if (!SSN9_FORMAT.test(value)) {
        return t("components.form.ssn9.errors.invalid");
      }
      return true;
    };
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isInitialValueScrubbed || $[14] !== scrubbedSsn9Value) {
    t6 = value_0 => {
      if (isInitialValueScrubbed && value_0 === scrubbedSsn9Value) {
        return;
      }
      return value_0;
    };
    $[13] = isInitialValueScrubbed;
    $[14] = scrubbedSsn9Value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== rules || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      validate: t5,
      setValueAs: t6,
      ...rules
    };
    $[16] = rules;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const ssnRules = t7;
  let t8;
  if ($[20] !== props.name || $[21] !== setValue || $[22] !== showScrubbedValue) {
    t8 = () => {
      if (showScrubbedValue) {
        setShowScrubbedValue(false);
        setValue(props.name, "" as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[20] = props.name;
    $[21] = setValue;
    $[22] = showScrubbedValue;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleFocus = t8;
  let t9;
  if ($[24] !== isInitialValueScrubbed || $[25] !== props.name || $[26] !== scrubbedSsn9Value || $[27] !== setValue || $[28] !== showScrubbedValue) {
    t9 = e => {
      if (!e.target.value && !showScrubbedValue && isInitialValueScrubbed) {
        setShowScrubbedValue(true);
        setValue(props.name, scrubbedSsn9Value as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[24] = isInitialValueScrubbed;
    $[25] = props.name;
    $[26] = scrubbedSsn9Value;
    $[27] = setValue;
    $[28] = showScrubbedValue;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleBlur = t9;
  let t10;
  if ($[30] !== scrubbedSsn9Value || $[31] !== showScrubbedValue) {
    t10 = showScrubbedValue ? {
      value: scrubbedSsn9Value
    } : {
      mask
    };
    $[30] = scrubbedSsn9Value;
    $[31] = showScrubbedValue;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const ssn4OverrideProps = t10;
  const t11 = isVisible ? "text" : "password";
  const t12 = isVisible ? IcoEyeCrossed16 : IcoEye16;
  const t13 = size === "compact" ? "tiny" : "default";
  let t14;
  if ($[33] !== isVisible || $[34] !== t) {
    t14 = isVisible ? t("components.form.ssn9.aria-label.hide") : t("components.form.ssn9.aria-label.show");
    $[33] = isVisible;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== isVisible) {
    t15 = () => setIsVisible(!isVisible);
    $[36] = isVisible;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t15) {
    t16 = <IconButton icon={t12} color="quaternary" size={t13} aria-label={t14} onClick={t15} />;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== handleBlur || $[44] !== handleFocus || $[45] !== props || $[46] !== size || $[47] !== ssn4OverrideProps || $[48] !== ssnRules || $[49] !== t11 || $[50] !== t16) {
    t17 = <FormInput {...props} size={size} type={t11} inputMode="numeric" autoComplete="off" placeholder="XXX-XX-XXXX" suffixComponent={t16} {...ssn4OverrideProps} rules={ssnRules} onFocusCapture={handleFocus} onBlur={handleBlur} />;
    $[43] = handleBlur;
    $[44] = handleFocus;
    $[45] = props;
    $[46] = size;
    $[47] = ssn4OverrideProps;
    $[48] = ssnRules;
    $[49] = t11;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}
const mask = {
  numericOnly: true,
  delimiters: ['-', '-'],
  blocks: [3, 2, 4]
};